import React, { useEffect, useState } from 'react'

import styled from 'styled-components'

import {
  Box,
  Button,
  HorizontalLine,
} from 'app/components/design-system-components'
import { SearchRounded } from 'app/components/design-system-components/icons/actions'
import { Input } from 'app/components/design-system-components/inputs'
import { CheckboxesList } from 'app/components/design-system-components/list-filters/CheckboxesList'
import { DropdownBodySectionHeader } from 'app/components/design-system-components/list-filters/DropdownBodySectionHeader'
import * as themeGetter from 'app/components/design-system-components/theme/getters'
import theme from 'app/match/applicationReportPDF/assets/theme'

const FilterDropdownBodyBox = styled(Box)`
  box-shadow: ${theme.boxShadows.dropdownDropped};
  background-color: ${themeGetter.color('white')};
  position: absolute;
  z-index: 10;
  max-height: ${theme.height[22]}px;
`

interface Manager {
  guidID: string
  profile?: {
    firstName?: string
    lastName?: string
    email?: string
  }
  isMe?: boolean
  [key: string]: any
}

interface OptionMeta {
  id: string
  displayText: string
}

interface Option {
  option: Manager
  optionMeta: OptionMeta
}

interface SelectedOptions {
  [key: string]: Option
}

interface ManagersOptionsListProps {
  listTitle: string
  options: Option[]
  selectedOptions: SelectedOptions
  onCheckBoxClicked: (checked: boolean, option: Option) => void
}

const ManagersOptionsList: React.FC<ManagersOptionsListProps> = ({
  listTitle,
  options,
  selectedOptions,
  onCheckBoxClicked,
}) => {
  if (!options || !options.length) return null
  return (
    <>
      <DropdownBodySectionHeader
        header={listTitle}
        containerProps={{ mb: theme.space[4] + 'px' }}
      />
      <CheckboxesList
        options={options}
        onCheckBoxClicked={onCheckBoxClicked}
        checkedOptions={selectedOptions}
      />
    </>
  )
}

interface ManagerSearchDropdownProps {
  managers?: Manager[]
  selectedManagerId?: string
  onManagerSelect?: (managerId: string | string[]) => void
  isLoading?: boolean
  isOpen?: boolean
  onClose?: () => void
  placeholder?: string
  managerListTitle?: string
  multiSelect?: boolean
}

/**
 * ManagerSearchDropdown - A reusable component for searching and selecting managers
 */
const ManagerSearchDropdown: React.FC<ManagerSearchDropdownProps> = ({
  managers = [],
  selectedManagerId = '',
  onManagerSelect,
  isLoading = false,
  isOpen = false,
  onClose,
  placeholder = 'Search for a contact',
  managerListTitle = 'All Contact',
  multiSelect = false,
}) => {
  const [searchText, setSearchText] = useState<string>('')
  const [selectedManager, setSelectedManager] = useState<SelectedOptions>({})

  useEffect(() => {
    if (!multiSelect && selectedManagerId) {
      const manager = managers.find((m) => m.guidID === selectedManagerId)
      if (manager) {
        setSelectedManager({
          [selectedManagerId]: {
            option: manager,
            optionMeta: {
              id: manager.guidID,
              displayText: `${manager?.profile?.firstName || ''} ${
                manager?.profile?.lastName || ''
              }`.trim(),
            },
          },
        })
      }
    }
  }, [selectedManagerId, managers, multiSelect])

  const onSearchTextChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(value)
  }

  // Transform managers into options format
  const managerOptions: Option[] = managers
    .map((manager) => {
      const { firstName = '', lastName = '' } = manager?.profile || {}
      return {
        option: manager,
        optionMeta: {
          id: manager.guidID,
          displayText: `${firstName} ${lastName}`.trim(),
        },
      }
    })
    .filter(({ optionMeta: { displayText } }) =>
      displayText.toLowerCase().includes(searchText.toLowerCase()),
    )

  // Separate "You" option and other managers
  const meOptionIndex = managerOptions.findIndex(
    ({ option: { isMe } }) => !!isMe,
  )

  const clonedOptions = [...managerOptions]
  const meOption: Option | null | undefined =
    meOptionIndex >= 0 ? clonedOptions.splice(meOptionIndex, 1)[0] : null

  const onCheckBoxClicked = (checked: boolean, option: Option) => {
    if (multiSelect) {
      // Handle multi-select logic
      if (checked) {
        setSelectedManager((prev) => ({
          ...prev,
          [option.optionMeta.id]: option,
        }))
      } else {
        setSelectedManager((prev) => {
          const newSelection = { ...prev }
          delete newSelection[option.optionMeta.id]
          return newSelection
        })
      }
    } else {
      // Handle single select logic - don't close immediately, wait for Select button
      if (checked) {
        setSelectedManager({
          [option.optionMeta.id]: option,
        })
      } else {
        setSelectedManager({})
      }
    }
  }

  const handleSelectManager = () => {
    const selectedIds = Object.keys(selectedManager)
    if (selectedIds.length > 0) {
      const selectedId: string | string[] = multiSelect
        ? selectedIds
        : selectedIds[0] || ''
      onManagerSelect && onManagerSelect(selectedId)
    } else {
      const emptyValue: string | string[] = multiSelect ? [] : ''
      onManagerSelect && onManagerSelect(emptyValue)
    }
    onClose && onClose()
  }

  if (!isOpen) return null

  return (
    <FilterDropdownBodyBox>
      <Box maxHeight="100%">
        <Box p={theme.space[4] + 'px'}>
          <Input
            id="manager-search"
            icon={<SearchRounded style={{ color: theme.colors.gray400 }} />}
            prefix={null}
            postfix={null}
            endedIcon={null}
            containerProps={{}}
            inputProps={{
              onChange: onSearchTextChange,
              placeholder,
              value: searchText,
            }}
          />
        </Box>
        {meOption ? (
          <Box maxHeight="200px" overflowY="auto">
            <ManagersOptionsList
              options={[meOption]}
              listTitle="You"
              onCheckBoxClicked={onCheckBoxClicked}
              selectedOptions={selectedManager}
            />
            <ManagersOptionsList
              options={clonedOptions}
              listTitle={managerListTitle}
              onCheckBoxClicked={onCheckBoxClicked}
              selectedOptions={selectedManager}
            />
          </Box>
        ) : (
          <ManagersOptionsList
            options={managerOptions}
            listTitle={managerListTitle}
            onCheckBoxClicked={onCheckBoxClicked}
            selectedOptions={selectedManager}
          />
        )}
      </Box>
      <HorizontalLine />
      <Box
        boxShadow={`${theme.space[0]}px -${theme.space[1]}px ${theme.dividedSpace[3]}px ${theme.space[0]}px ${theme.colors.searchAndSelectBoxShadow}`}
        p={theme.space[3] + 'px'}
      >
        <Button
          variant="primary"
          onClick={handleSelectManager}
          disabled={isLoading}
          sizeVariant="regular"
          width="100%"
        >
          {isLoading ? 'Selecting...' : 'Select'}
        </Button>
      </Box>
    </FilterDropdownBodyBox>
  )
}

export default ManagerSearchDropdown
export type { Manager, ManagerSearchDropdownProps }
